// frontend/src/components/athleteEditor/AccoladesEditor.jsx
//
// Add / edit / delete an athlete's accolades. Extracted verbatim from
// PlayerStatsPage.js so the admin edit panel can mount the same editor —
// before this, staff could toggle an accolade's verify seal on the admin page
// but had no way to create one, which left concierge athletes unable to have
// accolades entered for them at all.
//
// Addressing comes from the optional `getConfig` prop (see editorConfig.js):
// omitted on the athlete's own page, supplied by the admin panel so every
// request names the athlete being edited.
//
// The structured taxonomy below mirrors backend ACCOLADE_TYPES /
// ACCOLADE_LEVELS — keep them in sync.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCatalog } from '../../context/CatalogContext';
import Button from '../Button';
import VerifiedFlame from '../VerifiedFlame';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

export const ACCOLADE_TYPE_OPTIONS = [
  { value: 'honor', label: 'Honor / Award' },
  { value: 'ranking', label: 'Ranking / Rating' },
  { value: 'club_selection', label: 'Club / Team Selection' },
  { value: 'certification', label: 'Certification' },
];
export const ACCOLADE_LEVEL_OPTIONS = [
  { value: '', label: 'Level…' },
  { value: 'national', label: 'National' },
  { value: 'state', label: 'State' },
  { value: 'regional', label: 'Regional' },
  { value: 'conference', label: 'Conference' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'club_selection', label: 'Club Selection' },
  { value: 'school', label: 'School / Team' },
];
export const ACCOLADE_TYPE_LABEL =
  Object.fromEntries(ACCOLADE_TYPE_OPTIONS.map(o => [o.value, o.label]));
export const ACCOLADE_LEVEL_LABEL = Object.fromEntries(
  ACCOLADE_LEVEL_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));

const EMPTY_ACCOLADE = {
  accolade_text: '', year: '', accolade_type: 'honor', level: '',
  issuing_body: '', rating_value: '',
};

export default function AccoladesEditor({ sportName, getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const getConfig = resolveGetConfig(getConfigProp);

  const [accolades, setAccolades] = useState([]);
  const [form, setForm] = useState(EMPTY_ACCOLADE);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const showMsg = (text, type = '') => {
    setMessage(text); setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  // getConfig is rebuilt every render by the caller, so it is deliberately not
  // a dependency — depending on it would refetch on every parent render.
  const fetchAccolades = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/athletes/accolades`, getConfig());
      setAccolades(res.data || []);
    } catch (err) {
      console.error('Error fetching accolades:', err);
    }
    // eslint-disable-next-line
  }, [API_URL]);

  useEffect(() => { fetchAccolades(); }, [fetchAccolades]);

  const reset = () => { setForm(EMPTY_ACCOLADE); setEditingId(null); };

  const saveAccolade = async () => {
    if (!form.accolade_text.trim()) return;
    const isRanking = form.accolade_type === 'ranking';
    const payload = {
      accolade_text: form.accolade_text.trim(),
      year: form.year ? Number(form.year) : null,
      accolade_type: form.accolade_type || 'honor',
      level: form.level || null,
      issuing_body: form.issuing_body ? form.issuing_body.trim() : null,
      rating_value: isRanking && form.rating_value ? form.rating_value.trim() : null,
      sport_slug: catalog.sportSlugFor(sportName),
    };
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/athletes/accolades/${editingId}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/athletes/accolades`, payload, getConfig());
      }
      reset();
      fetchAccolades();
      showMsg(editingId ? 'Accolade updated' : 'Accolade added');
    } catch (err) {
      showMsg('Error saving accolade', 'error');
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      accolade_text: a.accolade_text || '',
      year: a.year || '',
      accolade_type: a.accolade_type || 'honor',
      level: a.level || '',
      issuing_body: a.issuing_body || '',
      rating_value: a.rating_value || '',
    });
  };

  const deleteAccolade = async (id) => {
    try {
      await axios.delete(`${API_URL}/athletes/accolades/${id}`, getConfig());
      if (editingId === id) reset();
      fetchAccolades();
      showMsg('Accolade removed');
    } catch (err) {
      console.error('Error deleting accolade:', err);
    }
  };

  const rankingServices = catalog.rankingServicesFor(catalog.sportSlugFor(sportName));
  const isRanking = form.accolade_type === 'ranking';
  const useServiceDropdown = isRanking && rankingServices.length > 0;
  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const sourceLabel = isRanking ? 'Ranking Service' : 'Issuing Body';

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Accolades</h2>
        <p className="settings-section__desc">
          Awards, honors, rankings, and selections. Add the level and source so
          coaches can weigh them at a glance.
        </p>

        <div className="form-row">
          <div className="form-field" style={{ flex: 2 }}>
            <label className="form-field__label" htmlFor="accolade_text">Accolade</label>
            <input
              id="accolade_text"
              type="text"
              className="form-field__input"
              value={form.accolade_text}
              onChange={(e) => setField({ accolade_text: e.target.value })}
              placeholder="e.g., All-State First Team"
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="accolade_type">Type</label>
            <select
              id="accolade_type"
              className="form-field__select"
              value={form.accolade_type}
              onChange={(e) => setField({ accolade_type: e.target.value })}
            >
              {ACCOLADE_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="accolade_level">Level</label>
            <select
              id="accolade_level"
              className="form-field__select"
              value={form.level}
              onChange={(e) => setField({ level: e.target.value })}
            >
              {ACCOLADE_LEVEL_OPTIONS.map(o => (
                <option key={o.value || 'none'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="accolade_source">{sourceLabel}</label>
            {useServiceDropdown ? (
              <select
                id="accolade_source"
                className="form-field__select"
                value={form.issuing_body}
                onChange={(e) => setField({ issuing_body: e.target.value })}
              >
                <option value="">Select service…</option>
                {rankingServices.map(r => (
                  <option key={r.key} value={r.label}>{r.label}</option>
                ))}
              </select>
            ) : (
              <input
                id="accolade_source"
                type="text"
                className="form-field__input"
                value={form.issuing_body}
                onChange={(e) => setField({ issuing_body: e.target.value })}
                placeholder="e.g., United Soccer Coaches"
              />
            )}
          </div>
          {isRanking && (
            <div className="form-field" style={{ maxWidth: 140 }}>
              <label className="form-field__label" htmlFor="accolade_rating">Rating / Rank</label>
              <input
                id="accolade_rating"
                type="text"
                className="form-field__input"
                value={form.rating_value}
                onChange={(e) => setField({ rating_value: e.target.value })}
                placeholder="e.g., 5-star"
              />
            </div>
          )}
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label" htmlFor="accolade_year">Year</label>
            <select
              id="accolade_year"
              className="form-field__select"
              value={form.year}
              onChange={(e) => setField({ year: e.target.value })}
            >
              <option value="">Year…</option>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveAccolade}>
            {editingId ? 'Save Changes' : 'Add Accolade'}
          </Button>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Cancel
            </Button>
          )}
        </div>

        {accolades.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>
            No accolades added yet.
          </p>
        )}
        {accolades.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {accolades.map(a => (
              <div key={a.id} className="accolade-row">
                <div className="accolade-row__body">
                  <span className="accolade-row__title">
                    {a.accolade_text}
                    {a.verified && <VerifiedFlame />}
                  </span>
                  <span className="accolade-row__meta">
                    {ACCOLADE_LEVEL_LABEL[a.level] && (
                      <span className="accolade-chip">{ACCOLADE_LEVEL_LABEL[a.level]}</span>
                    )}
                    {a.accolade_type && a.accolade_type !== 'honor' && (
                      <span className="accolade-chip">{ACCOLADE_TYPE_LABEL[a.accolade_type]}</span>
                    )}
                    {a.issuing_body && <span className="accolade-row__src">{a.issuing_body}</span>}
                    {a.rating_value && <span className="accolade-row__src">{a.rating_value}</span>}
                    {a.year && <span className="accolade-row__src">{a.year}</span>}
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(a)}>
                  Edit
                </Button>
                <Button type="button" variant="danger" size="sm" onClick={() => deleteAccolade(a.id)}>
                  Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
